import React from 'react';
import { observable, action } from 'mobx';
import { FormattedMessage } from 'react-intl';
import Styled from 'styled';

import Image from 'lib/images/image-view';
import withCompute from 'lib/mobx/with-compute';

import BlogLink from 'four-nets/blog/blog-link';
import ErrorPage from 'four-nets/errors/error-page';
import TextShortener from 'four-nets/ui-v2/text-shortener/text-shortener';

import defaultStyles from './blog-selection-view.scss';

export { BlogSelectionViewModel };

@withCompute()
class BlogSelectionViewModel {
    @observable error?: Error;
    @observable articles: {
        href: string;
        label: string;
        image: {
            src: string;
            srcset: string;
        };
    }[] = [];

    dataSource: any = null;

    constructor(dataSource: any) {
        this.dataSource = dataSource;
    }

    toJSON() {
        return {
            articles: this.articles,
        };
    }

    fromJSON(json: any) {
        this.articles = json.articles;
    }

    @action loadMostReadDidSucceed = (mostRead: any) => {
        this.articles = mostRead.results.map((article: any) => {
            return {
                href: `/magazine/${article.slug}/`,
                label: article.title.rendered,
                image: {
                    src: article.featuredMedia.src,
                    srcset: article.featuredMedia.srcset,
                },
            };
        });
    };

    @action loadDidFail = (error: any) => {
        this.articles = [];
        this.error = error;
    };

    load = () => {
        return this.dataSource.postStore
            .loadMostRead({
                fields: JSON.stringify([
                    'title',
                    'slug',
                    ['featured_media', ['media_details', 'alt_text', 'source_url', 'guid']],
                ]),
            })
            .then(this.loadMostReadDidSucceed)
            .catch(this.loadDidFail);
    };
}

interface Props {
    styles: typeof defaultStyles;
    blogSelectionViewModel: BlogSelectionViewModel;
}

const BlogSelectionView: React.FC<Props> = React.memo((props: Props) => {
    const { styles, blogSelectionViewModel } = props;
    const { error, articles } = blogSelectionViewModel;

    return (
        <ErrorPage error={error}>
            <div className={styles.wrapper}>
                <FormattedMessage id='MOST_READ_ARTICLES'>
                    {msg => <span className={styles.wrapperTitle}>{msg}</span>}
                </FormattedMessage>
                {articles.map(article => (
                    <BlogLink key={article.href} to={article.href} className={styles.articleLink}>
                        <div className={styles.articleImage}>
                            <Image
                                sizes={{ xs: 150 }}
                                aspectFill={true}
                                srcSet={article.image.srcset}
                                src={article.image.src}
                                alt={article.label}
                                linkify={false}
                            />
                        </div>
                        <span className={styles.articleTitle}>
                            <TextShortener text={article.label} textLength={180} />
                        </span>
                    </BlogLink>
                ))}
            </div>
        </ErrorPage>
    );
});

export default Styled(defaultStyles)(BlogSelectionView);
